import React from 'react';
import { cn } from '@cotera/client/v0/lib/utils';

/**
 * Horizontal scroll box for a row of filter chips.
 *
 * The chip groups above a list (the Library's types, Automations' trigger
 * sources) are `w-max` and `whitespace-nowrap` by design — a chip that wraps
 * mid-label, or a row that reflows as you switch between them, reads worse than
 * one that runs off the edge. What that costs on a phone is the whole page: six
 * labels and a divider clear 400px, and a `w-max` child inside the page's
 * flex column drags the column itself wider than the viewport, so *every* row
 * below could be scrolled sideways.
 *
 * Capping the group at the row's width and giving it its own scroller keeps the
 * chips on one line and keeps the overflow local to them. On a desktop, where
 * they already fit, this is inert.
 */
export const FilterChipScroller: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => (
  <div
    className={cn(
      'max-w-full min-w-0 overflow-x-auto',
      // `px-1`/`-mx-1`: the scroll box clips, and a focus ring on the first or
      // last chip sits outside that chip's border box. Without the gutter,
      // tabbing to either end showed a ring with one side shaved off.
      '-mx-1 px-1',
      // No scrollbar. It would sit under the chips at rest on every platform
      // that reserves space for one, adding a stripe of chrome to a control
      // band that fits without it on all but the narrowest screens — and the
      // half-shown chip at the edge is a clearer overflow cue than a 4px bar.
      'no-scrollbar',
      className
    )}
  >
    {children}
  </div>
);
